import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { Bookmark, Search as SearchIcon, X } from "lucide-react";

import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import { Header } from "../../../components/Header/Header";
import {
  deleteSavedSearch,
  getSavedSearch,
  listSavedSearches,
  markSavedSearchViewed,
  updateSavedSearch,
} from "../../../services/savedSearchesApi";
import { fetchTenderFacets, searchTenders } from "../../../services/tendersApi";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import AlertSettingsModal from "./AlertSettingsModal";
import SaveSearchModal from "./SaveSearchModal";
import TenderFilters, {
  DEFAULT_ORDERING,
  EMPTY_FILTERS,
  buildSearchParams,
  countActiveFilters,
  filtersFromUrl,
  filtersToUrl,
} from "./TenderFilters";
import TenderRail from "./TenderRail";
import TenderModeSwitch from "./TenderModeSwitch";
import TenderRow from "./TenderRow";
import TenderStats from "./TenderStats";
import { CONTENT_CLASS, Callout, RAIL_CLASS, SortSelect, TOP_BAR_CLASS } from "./TenderUI";

const PAGE_SIZE = 20;
const RELEVANCE = "relevance";

// Typing moves the sort to best match and clearing moves it back, but only ever
// between those two. A sort the user picked themselves is theirs to keep.
const orderingFor = (text, current) => {
  const typing = text.trim().length > 0;
  if (typing && current === DEFAULT_ORDERING) return RELEVANCE;
  if (!typing && current === RELEVANCE) return DEFAULT_ORDERING;
  return current;
};

// A saved search's `state` round-trips through Postgres `jsonb`, which does
// not preserve object key order — the same filters object comes back with
// its keys in a different order than it was saved in. Plain JSON.stringify
// would read that as "changed" every time, so object keys are sorted before
// comparing; array order is left alone, since that's a real (if rare)
// difference for a list a person actually reordered.
const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

const FALLBACK_ORDERINGS = [
  { value: "closing_soon", label: "Closing soonest" },
  { value: "recently_posted", label: "Recently posted" },
  { value: "relevance", label: "Best match" },
];

// Skeleton mirroring the real row, so the page does not reflow when results land.
function RowSkeleton() {
  return (
    <div className="border border-[#E5E7EB] rounded-xl bg-white px-[18px] py-4 mb-2.5 flex gap-5">
      <div className="flex-1 min-w-0">
        <div className="flex gap-2 mb-3">
          <div className="h-[22px] w-20 rounded-full bg-[#F4F4F5] animate-pulse" />
          <div className="h-[22px] w-28 rounded-full bg-[#F4F4F5] animate-pulse" />
        </div>
        <div className="h-4 w-3/4 rounded bg-[#F4F4F5] animate-pulse mb-2" />
        <div className="h-3 w-1/2 rounded bg-[#F4F4F5] animate-pulse" />
      </div>
      <div className="shrink-0 min-w-[122px] pl-5 border-l border-[#F1F1F3] flex flex-col items-end justify-center gap-2">
        <div className="h-3 w-12 rounded bg-[#F4F4F5] animate-pulse" />
        <div className="h-4 w-16 rounded bg-[#F4F4F5] animate-pulse" />
      </div>
    </div>
  );
}

// Per browser tab, not per browser: a search you were mid-way through should
// survive walking to another screen and back, but should not still be waiting
// for you in a window you open next week.
//
// Every read and write is guarded: private windows and blocked site data make
// sessionStorage throw on access, and a filter rail must not take the page
// down with it.
const REMEMBERED = "tenderSearch:lastSearch";

const readRemembered = () => {
  try {
    return sessionStorage.getItem(REMEMBERED);
  } catch {
    return null;
  }
};

const writeRemembered = (value) => {
  try {
    sessionStorage.setItem(REMEMBERED, value);
  } catch {
    /* nothing to remember it with */
  }
};

const forgetRemembered = () => {
  try {
    sessionStorage.removeItem(REMEMBERED);
  } catch {
    /* nothing to forget it from */
  }
};

export default function TenderSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMenuOpen } = useOutletContext() || {};

  // The URL holds the search, not component state: this screen unmounts every
  // time a tender is opened, and rebuilding a filter set by hand each time is
  // the fastest way to make a search screen unusable.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKey = searchParams.toString();
  const { filters, ordering, keywords: urlKeywords } = useMemo(
    () => filtersFromUrl(urlKey),
    [urlKey]
  );

  // Typing stays local so each keystroke renders immediately; the URL catches up
  // on the same debounce as the request.
  const [keywords, setKeywords] = useState(urlKeywords);

  // Only the box may push a keyword into the URL; a URL changing for any other
  // reason flows one way, into the box. Both ways and the page never settles.
  const typedRef = useRef(false);
  const changeKeywords = useCallback((value) => {
    typedRef.current = true;
    setKeywords(value);
  }, []);

  const writeUrl = useCallback(
    (next) => {
      setSearchParams(
        filtersToUrl(next.filters ?? filters, {
          keywords: next.keywords ?? keywords,
          ordering: next.ordering ?? ordering,
        }),
        { replace: true }
      );
    },
    [filters, keywords, ordering, setSearchParams]
  );

  const setFilters = useCallback(
    (next) => writeUrl({ filters: typeof next === "function" ? next(filters) : next }),
    [filters, writeUrl]
  );

  const setOrdering = useCallback((value) => writeUrl({ ordering: value }), [writeUrl]);

  // The sidebar link lands on a bare /tender-search, so stepping out to a
  // project and back used to drop the whole search. Remembered here and put
  // back, and only forgotten when the user clears it themselves.
  //
  // A URL that carries a search always wins: a shared link has to open the
  // search it names, not whatever this browser was last looking at.
  const restored = useRef(false);

  // Nothing is fetched until this settles, or a bare URL fires a whole search
  // for the unfiltered corpus before the remembered one is put back.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    // A link naming a saved search is opened by the effect further down, which sets `ready` itself.
    if (searchParams.get("saved")) return;
    const saved = urlKey ? null : readRemembered();
    if (saved) setSearchParams(new URLSearchParams(saved), { replace: true });
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, setSearchParams]);

  // Emptying the search is itself worth remembering. Skipping the write left the
  // old one behind, so clearing the box and reloading brought the word back.
  // Gated on `ready` so this cannot wipe the value the restore is about to read.
  useEffect(() => {
    if (!ready || searchParams.get("saved")) return;
    if (urlKey) writeRemembered(urlKey);
    else forgetRemembered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, urlKey]);

  const clearAll = useCallback(() => {
    forgetRemembered();
    setFilters(EMPTY_FILTERS);
  }, [setFilters]);

  // Back and forward move the URL under us; the input has to follow.
  useEffect(() => {
    setKeywords((current) => (current === urlKeywords ? current : urlKeywords));
  }, [urlKeywords]);

  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [excluded, setExcluded] = useState(null);
  const [facets, setFacets] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Saved searches: a private, per-user list pinned at the top of the rail.
  // `activeSnapshot` is the {keywords, ordering, filters} the active one held
  // when it was opened — compared against the live search below to decide
  // whether "Update search" should show at all.
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchesLoading, setSavedSearchesLoading] = useState(true);
  const [activeSavedSearchId, setActiveSavedSearchId] = useState(null);
  const [activeSnapshot, setActiveSnapshot] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [alertsTarget, setAlertsTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refreshSavedSearches = useCallback(async () => {
    try {
      const rows = await listSavedSearches();
      setSavedSearches(rows);
    } catch {
      // The rail simply shows none — not worth blocking the whole page over.
    } finally {
      setSavedSearchesLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSavedSearches();
  }, [refreshSavedSearches]);

  // Opening a saved search applies its filters exactly the way a shared link
  // would, then marks it viewed so its badge clears — but only for a genuine
  // open, never for merely being listed in the rail.
  const handleOpenSavedSearch = useCallback(
    (row) => {
      const snapshot = row.state || { keywords: "", ordering: DEFAULT_ORDERING, filters: EMPTY_FILTERS };
      writeUrl({
        filters: snapshot.filters || EMPTY_FILTERS,
        keywords: snapshot.keywords || "",
        ordering: snapshot.ordering || DEFAULT_ORDERING,
      });
      setKeywords(snapshot.keywords || "");
      setActiveSavedSearchId(row.id);
      setActiveSnapshot(snapshot);
      setSavedSearches((prev) => prev.map((r) => (r.id === row.id ? { ...r, new_count: 0 } : r)));
      markSavedSearchViewed(row.id).catch(() => {});
    },
    [writeUrl]
  );

  // Email and bell links arrive as ?saved=<id>&org=<workspace>; opening it rewrites the URL to the real filters.
  const linkedSavedSearchId = searchParams.get("saved");
  const linkedOrgId = searchParams.get("org");
  const openedLinkRef = useRef(null);
  useEffect(() => {
    if (!linkedSavedSearchId) {
      openedLinkRef.current = null;
      return;
    }
    if (openedLinkRef.current === linkedSavedSearchId) return;
    openedLinkRef.current = linkedSavedSearchId;
    setReady(false);

    if (linkedOrgId && linkedOrgId !== Cookies.get("selected_org_id")) {
      let workspaces = [];
      try {
        workspaces = JSON.parse(localStorage.getItem("cachedWorkspaces") || "[]");
      } catch {}
      const workspace = Array.isArray(workspaces) && workspaces.find((w) => String(w.id) === linkedOrgId);
      if (workspace) {
        localStorage.setItem("activeWorkspace", JSON.stringify(workspace));
        localStorage.setItem("activeWorkspaceId", String(workspace.id));
        Cookies.set("selected_org_id", String(workspace.id), { expires: 365 });
        window.dispatchEvent(new CustomEvent("workspace-changed", { detail: workspace }));
        refreshSavedSearches();
      }
    }

    getSavedSearch(linkedSavedSearchId)
      .then(handleOpenSavedSearch)
      .catch((err) => {
        setSearchParams(new URLSearchParams(), { replace: true });
        showErrorToast(
          err?.response?.status === 404
            ? "That saved search no longer exists, so no filters were applied."
            : "Could not open that saved search. Please try again."
        );
      })
      .finally(() => setReady(true));
  }, [linkedSavedSearchId, linkedOrgId, handleOpenSavedSearch, setSearchParams, refreshSavedSearches]);

  const handleSavedSearchCreated = useCallback((row) => {
    setSavedSearches((prev) => [row, ...prev]);
    setActiveSavedSearchId(row.id);
    setActiveSnapshot(row.state);
  }, []);

  const handleUpdateSearch = useCallback(async () => {
    if (activeSavedSearchId == null) return;
    const nextState = { keywords: urlKeywords, ordering, filters };
    try {
      const updated = await updateSavedSearch(activeSavedSearchId, { state: nextState });
      setSavedSearches((prev) => prev.map((r) => (r.id === activeSavedSearchId ? updated : r)));
      setActiveSnapshot(updated.state);
      showSuccessToast("Saved search updated");
    } catch {
      showErrorToast("Could not update this saved search. Please try again.");
    }
  }, [activeSavedSearchId, urlKeywords, ordering, filters]);

  const confirmDeleteSavedSearch = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSavedSearch(deleteTarget.id);
      setSavedSearches((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      if (activeSavedSearchId === deleteTarget.id) {
        setActiveSavedSearchId(null);
        setActiveSnapshot(null);
      }
      showSuccessToast(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch {
      showErrorToast("Could not delete this saved search. Please try again.");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, activeSavedSearchId]);

  // Compared on the settled URL keywords, not the box mid-keystroke, for the
  // same reason the search request itself waits for it.
  const filtersChanged = useMemo(() => {
    if (activeSavedSearchId == null || !activeSnapshot) return false;
    const current = { keywords: urlKeywords, ordering, filters };
    return stableStringify(current) !== stableStringify(activeSnapshot);
  }, [activeSavedSearchId, activeSnapshot, urlKeywords, ordering, filters]);

  // Guards against an older, slower response overwriting a newer one — easy to
  // trigger by typing quickly, and it makes the list show results for a query
  // the user has already moved on from.
  const requestRef = useRef(0);

  // From the settled URL, never the box mid-keystroke: the URL write is already
  // debounced, so a half-typed word is not searched and then searched again.
  //
  // Memoised on the content, not the objects: writing the search back to the URL
  // rebuilds `filters`, and identity alone would fire a second identical request.
  const paramsKey = JSON.stringify(
    buildSearchParams(filters, { keywords: urlKeywords, ordering })
  );
  const params = useMemo(() => JSON.parse(paramsKey), [paramsKey]);

  const load = useCallback(
    async (offset = 0, signal) => {
      const ticket = ++requestRef.current;
      offset === 0 ? setLoading(true) : setLoadingMore(true);
      setError(null);

      try {
        const data = await searchTenders(params, { limit: PAGE_SIZE, offset, signal });
        if (ticket !== requestRef.current) return; // a newer search superseded this
        setResults((prev) => (offset === 0 ? data.results : [...prev, ...data.results]));
        setCount(data.count);
        setHasMore(data.has_more);
        setExcluded(data.excluded);
      } catch (err) {
        if (signal?.aborted || ticket !== requestRef.current) return;
        setError(
          err?.response?.status === 403
            ? "Tender search is not available for this workspace."
            : "Could not load tenders. Please try again."
        );
      } finally {
        if (ticket === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [params]
  );

  // Counts need the list's guard too: without it the rail showed whichever
  // response landed last, which is not the one sent last.
  const facetRef = useRef(0);

  const loadFacets = useCallback(
    async (signal) => {
      const ticket = ++facetRef.current;
      try {
        const data = await fetchTenderFacets(params, { signal });
        if (ticket === facetRef.current) setFacets(data);
      } catch {
        if (!signal?.aborted && ticket === facetRef.current) setFacets(null);
      }
    },
    [params]
  );

  // Both fire together so the list and its counts describe the same search, but
  // land separately so the list waits on nothing. Superseded ones are cancelled.
  useEffect(() => {
    if (!ready || linkedSavedSearchId) return undefined;
    const controller = new AbortController();
    load(0, controller.signal);
    loadFacets(controller.signal);
    return () => controller.abort();
  }, [ready, linkedSavedSearchId, load, loadFacets]);

  // The typed query joins the URL once typing settles, carrying its sort in the
  // same write so the two can never race.
  useEffect(() => {
    if (!typedRef.current) return undefined;
    if (keywords === urlKeywords) {
      typedRef.current = false;
      return undefined;
    }
    const t = setTimeout(() => {
      typedRef.current = false;
      writeUrl({ keywords, ordering: orderingFor(keywords, ordering) });
    }, 400);
    return () => clearTimeout(t);
  }, [keywords, urlKeywords, ordering, writeUrl]);

  const orderings = facets?.orderings?.length ? facets.orderings : FALLBACK_ORDERINGS;
  const sources = facets?.capabilities?.sources || [];
  // Only an empty screen gets skeletons. Once there are rows, a filter change
  // dims them instead of replacing them, so nothing flashes or jumps.
  const firstLoad = loading && results.length === 0;
  const activeCount = countActiveFilters(filters);
  const industryFilterOn = filters.category.length > 0;
  // A saved search with nothing behind it — no keyword, no filter — doesn't
  // say anything about what the person actually cares about, so there's
  // nothing meaningful to name and save yet.
  const canSaveSearch = activeCount > 0 || keywords.trim().length > 0;

  return (
    <div className="flex flex-col h-full w-full font-outfit overflow-hidden bg-[#FAFAFA]">
      <div className="flex-shrink-0 bg-white">
        <Header name="Tender Search" icon="" isMenuOpen={isMenuOpen} onClick={() => {}} />
      </div>

      {/* Rail and results scroll independently: the filters have to stay put
          while a long list of tenders moves under them. */}
      <div className="flex-1 flex min-h-0">
        <aside data-tour="tender-rail" className={RAIL_CLASS}>
          <TenderRail
            filters={filters}
            onChange={setFilters}
            facets={facets}
            activeCount={activeCount}
            onClearAll={clearAll}
            savedSearches={savedSearches}
            savedSearchesLoading={savedSearchesLoading}
            activeSavedSearchId={activeSavedSearchId}
            filtersChanged={filtersChanged}
            onOpenSavedSearch={handleOpenSavedSearch}
            onDeleteSavedSearch={setDeleteTarget}
            onEditAlerts={setAlertsTarget}
            onUpdateSearch={handleUpdateSearch}
            onManageClick={() => navigate("/tender-search/saved")}
          />
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          <div className={TOP_BAR_CLASS}>
            <div className="flex items-center gap-3">
              <TenderModeSwitch active="search" />
              <div data-tour="tender-search" className="relative flex-1 max-w-[620px]">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" size={18} />
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => changeKeywords(e.target.value)}
                  placeholder="Search by title, agency, solicitation number or description..."
                  aria-label="Search tenders"
                  className="w-full h-[35px] font-outfit text-[14px] leading-[20px] pl-10 pr-9
                             border border-[#00000036] rounded-md bg-white text-[#18101C]
                             outline-none transition-all box-border
                             focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE]
                             placeholder:text-[#9CA3AF]"
                />
                {keywords && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => changeKeywords("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#52525B]"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <button
                type="button"
                data-tour="tender-save"
                onClick={() => setSaveModalOpen(true)}
                disabled={firstLoad || !canSaveSearch}
                title={
                  firstLoad
                    ? "Waiting for the first results..."
                    : !canSaveSearch
                      ? "Search for something or turn on a filter first"
                      : undefined
                }
                className="inline-flex items-center gap-1.5 h-[35px] font-outfit text-[13px] font-semibold
                           text-[#52525B] border border-[#00000024] rounded-md px-3 bg-white
                           hover:bg-gray-50 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Bookmark className="w-3.5 h-3.5" />
                Save search
              </button>

              <SortSelect id="tender-sort" value={ordering} onChange={setOrdering} options={orderings} />
            </div>

            {/* The rail is hidden below lg, so the chip row stands in for it.
                Without this there is no way to filter on a narrow window. */}
            <div className="lg:hidden mt-3 flex items-center gap-2 flex-wrap">
              <TenderFilters filters={filters} onChange={setFilters} facets={facets} />
              {/* The rail carries Clear all on wide screens; without this the
                  narrow layout would have no way to undo the lot. */}
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 font-outfit text-[12px] font-semibold
                             text-[#BB42AE] border border-[#F0D7ED] bg-[#FDF6FC] rounded-md px-2 py-1
                             hover:bg-[#F8ECF6] transition-colors"
                >
                  <X className="w-3 h-3" strokeWidth={2.5} />
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pt-4">
            <div className={CONTENT_CLASS}>
              <TenderStats
                stats={facets?.counts?.stats}
                filters={filters}
                onChange={setFilters}
                loading={firstLoad}
              />

              {/* Scrolls with the list, as the design has it. Pinned, it sat
                  over the rows and put Clear in the way of what it clears. */}
              <div className="flex items-center justify-between gap-4 flex-wrap pt-0.5 pb-3">
                <div className="font-outfit text-[14px] text-[#71717A]">
                  {firstLoad ? (
                    "Searching..."
                  ) : (
                    <>
                      <b className="text-[#18101C] font-semibold">{count.toLocaleString()}</b>{" "}
                      tender{count === 1 ? "" : "s"} match these filters
                    </>
                  )}
                </div>
                {/* Clearing lives at the top of the rail now, beside the filters
                    themselves, rather than here among the results. */}
                <div className="flex items-center gap-3">
                  {sources.length > 0 && (
                    <span className="font-outfit text-[12px] text-[#9CA3AF]">
                      {sources.map((s) => s.name).join(", ")}
                    </span>
                  )}
                </div>
              </div>

              {error && <Callout tone="danger">{error}</Callout>}

              {/* The backend reports what a filter is hiding rather than letting it
                  vanish. Staying silent is how a user misses work they could have won. */}
              {excluded && (
                <Callout
                  tone="amber"
                  action="Include them"
                  onAction={() => setFilters((f) => ({ ...f, include_unclassified: true }))}
                >
                  <b className="font-semibold">{excluded.count.toLocaleString()} more tenders</b> match
                  your filters but have no industry listed by the agency.
                </Callout>
              )}

              {/* The other direction of the same honesty: with the default on, an
                  industry filter is quietly wider than it looks. */}
              {industryFilterOn && filters.include_unclassified && (
                <Callout
                  action="Exclude them"
                  onAction={() => setFilters((f) => ({ ...f, include_unclassified: false }))}
                >
                  Tenders the agency left without an industry are included, so this filter never hides
                  them.
                </Callout>
              )}

              {/* Same principle for attachments: the bulk feed cannot see them, so a
                  "has documents" filter must say what it could not check. */}
              {filters.has_documents && facets?.counts?.unknown_documents > 0 && (
                <Callout>
                  {facets.counts.unknown_documents.toLocaleString()} of these have not had their
                  attachments listed yet. Open one to check. Only{" "}
                  {facets.counts.with_documents.toLocaleString()} are confirmed to have documents.
                </Callout>
              )}

              {filters.state.length > 0 && (
                <Callout>
                  Tenders with no place of performance published are excluded while a location filter
                  is on. Most federal tenders do not publish one.
                </Callout>
              )}

              {firstLoad && (
                <div>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <RowSkeleton key={i} />
                  ))}
                </div>
              )}

              {!loading && !error && results.length === 0 && (
                <div className="border border-dashed border-[#D6D6DB] rounded-xl bg-white py-14 px-6 text-center">
                  <div className="w-11 h-11 rounded-full bg-[#F9F2F8] grid place-items-center mx-auto mb-3">
                    <SearchIcon className="w-5 h-5 text-[#BB42AE]" />
                  </div>
                  <p className="font-outfit text-[14px] text-[#52525B] font-medium">
                    No tenders match these filters.
                  </p>
                  <p className="font-outfit text-[13px] text-[#9CA3AF] mt-1">
                    Try removing a filter, or widen the closing date range.
                  </p>
                  {activeCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="mt-4 font-outfit text-[14px] font-medium text-[#BB42AE] border border-[#BB42AE]
                                 bg-white hover:bg-[#F8F3FF] rounded-md px-4 py-1.5 transition-colors"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}

              {/* Kept on screen while the next set loads. Tearing the list down
                  on every tick of a filter made the page flash, and the rows you
                  were reading jumped as they came back. */}
              {results.length > 0 && (
                <div
                  data-tour="tender-results"
                  aria-busy={loading}
                  className={`transition-opacity duration-150 ${
                    loading ? "opacity-50" : "opacity-100"
                  }`}
                >
                  {results.map((t) => (
                    <TenderRow
                      key={t.id}
                      tender={t}
                      onOpen={(x) =>
                        navigate(`/tender-search/${x.id}`, {
                          state: { from: location.search },
                        })
                      }
                    />
                  ))}
                </div>
              )}

              {!loading && hasMore && (
                <div className="text-center pt-4 pb-6">
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => load(results.length)}
                    className="font-outfit text-[14px] font-medium border border-[#00000036] rounded-md
                               px-4 py-2 bg-white text-[#52525B] hover:bg-gray-50 disabled:opacity-60"
                  >
                    {loadingMore ? "Loading..." : "Load more tenders"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SaveSearchModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        filters={filters}
        keywords={urlKeywords}
        ordering={ordering}
        resultCount={count}
        facets={facets}
        onSaved={handleSavedSearchCreated}
      />

      <AlertSettingsModal
        open={alertsTarget != null}
        savedSearch={alertsTarget}
        onClose={() => setAlertsTarget(null)}
        onSaved={(updated) =>
          setSavedSearches((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
        }
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete saved search"
          message={`"${deleteTarget.name}" and its alert settings will be deleted. This can't be undone.`}
          confirmLabel="Delete"
          danger
          busy={deleting}
          onConfirm={confirmDeleteSavedSearch}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
